import { Component, OnInit, ViewChild } from '@angular/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
    MatDatepickerModule,
    MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CommonModule, NgIf } from '@angular/common';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    ASC,
    COUNT,
    DAY,
    ISSORTASCENDING,
    MONTH,
    OFFSET,
    SORTPROPERTY,
    WEEK,
    YEAR,
} from 'src/app/constants/queryParamConstants';
import { ReportsService } from 'src/app/services/reports.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { VisibleDirective } from 'src/app/directives/visible.directive';
import { ReportTypes } from 'src/app/enums/report-types';
import saveAs from 'file-saver';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { OFFSET_START } from 'src/app/constants/searchConstants';
import { Page } from 'src/app/models/page.model';
import { Bench } from 'src/app/models/report-models/bench.models';
import { format, getISOWeek, parseISO } from 'date-fns';
import { CSVMIMETYPE } from 'src/app/constants/exportConstants';

interface IFilterFormControls {
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    dayOfTheWeek: FormControl<string | null>;
    hours: FormControl<string | null>;
    notes: FormControl<string | null>;
    date: FormControl<Date | null>;
}

@Component({
    selector: 'app-bench-notes-report',
    standalone: true,
    imports: [
        NgIf,
        MatProgressBarModule,
        MatRadioModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        CommonModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatTooltipModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatCheckboxModule,
        VisibleDirective,
        MatSortModule,
        MatCardModule,
    ],
    templateUrl: './bench-notes-report.component.html',
    styleUrl: './bench-notes-report.component.scss',
})
export class BenchNotesReportComponent implements OnInit {
    reports: Bench;
    reportTypes = ReportTypes;
    loadingReport = false;
    requestFailed = false;
    queryParams = new Map<string, string | boolean | number>();
    displayedColumns = ['FirstName', 'LastName', 'DayofWeek', 'Hours', 'Notes'];
    count: number;
    pageIndex: number = 0;
    sortAscending: string = null;
    sortProperty: string = null;
    rangeDates: boolean = false;

    dateForm = new FormGroup({
        date: new FormControl(new Date()),
    });

    filterForm: FormGroup<IFilterFormControls> = new FormGroup({
        firstName: new FormControl(null),
        lastName: new FormControl(null),
        dayOfTheWeek: new FormControl(null),
        hours: new FormControl(null),
        notes: new FormControl(null),
        date: new FormControl(new Date()),
    });

    page = new Page<Bench>({
        countRequested: 10,
        offsetRequested: 0,
        items: [],
        totalCount: 0,
    });

    dataSource: MatTableDataSource<Bench>;
    @ViewChild(MatSort) sort: MatSort;

    range = new FormGroup({
        fromDate: new FormControl<Date | string | null>(null, [
            Validators.required.bind(this),
        ]),
        toDate: new FormControl<Date | string | null>(null, [
            Validators.required.bind(this),
        ]),
    });

    constructor(
        private reportService: ReportsService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.checkMappedDate();

        this.getBenchReport();

        this.filterForm.controls.hours.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => this.getBenchReport());
    }

    setQueryParams(): void {
        if (this.pageIndex) {
            this.queryParams.set(
                OFFSET,
                this.pageIndex * this.page.countRequested,
            );
        } else {
            this.queryParams.set(OFFSET, OFFSET_START);
        }

        this.queryParams.set(COUNT, this.page.countRequested);

        this.queryParams.set(
            WEEK,
            getISOWeek(this.filterForm.controls.date.value).toString(),
        );
        this.queryParams.set(
            YEAR,
            this.filterForm.controls.date.value.getFullYear().toString(),
        );

        this.updateUrl();
    }

    checkMappedDate() {
        const map = this.route.snapshot.queryParamMap;

        if (map.has(DAY) && map.has(MONTH) && map.has(YEAR)) {
            const day = Number(map.get(DAY));
            const month = Number(map.get(MONTH));
            const year = Number(map.get(YEAR));
            const newDate = new Date(year, month, day);

            this.dateForm.controls.date.setValue(newDate);

            this.filterForm.controls.date.setValue(newDate);
        }
    }

    getBenchReport(): void {
        this.loadingReport = true;
        this.setQueryParams();

        this.reportService.get(['bench'], this.queryParams).subscribe(
            (response: Page<Bench>) => {
                this.page = response;
                this.dataSource = new MatTableDataSource(this.page.items);
                this.dataSource.sort = this.sort;
                this.loadingReport = false;
            },
            () => {
                this.requestFailed = true;
                this.loadingReport = false;
            },
        );
    }

    changePage(event: PageEvent): void {
        this.changePageIndex(event.pageIndex);
    }

    changePageIndex(index: number) {
        this.pageIndex = index;
        this.getBenchReport();
    }

    sortChange(sort: Sort): void {
        this.queryParams.set(
            ISSORTASCENDING,
            sort.direction.toUpperCase() == ASC ? true : false,
        );
        this.queryParams.set(SORTPROPERTY, sort.active);

        this.sortAscending =
            sort.direction.toUpperCase() == ASC ? 'true' : 'false';
        this.sortProperty = sort.active;

        this.updateUrl();
        this.getBenchReport();
    }

    dateChange(event: MatDatepickerInputEvent<Date>): void {
        this.filterForm.controls.date.setValue(event.value);
        this.getBenchReport();
    }

    exportReport(): void {
        this.setQueryParams();
        this.reportService
            .exportReport(['bench', 'export'], this.queryParams)
            .subscribe({
                next: (buffer: Blob) => {
                    const data: Blob = new Blob([buffer], {
                        type: CSVMIMETYPE,
                    });
                    saveAs(data, 'bench-report.csv');
                    this.loadingReport = false;
                },
                error: () => {
                    this.requestFailed = true;
                    this.loadingReport = false;
                },
            });
    }

    updateUrl(): void {
        void this.router.navigate([]);

        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                report: this.reportTypes.Billing,
                count: this.page.countRequested,
                day: this.filterForm.controls.date.value.getDate().toString(),
                month: this.filterForm.controls.date.value
                    .getMonth()
                    .toString(),
                year: this.filterForm.controls.date.value
                    .getFullYear()
                    .toString(),
                firstName: this.filterForm.controls.firstName.value,
                lastName: this.filterForm.controls.lastName.value,
                dayOfTheWeek: this.filterForm.controls.dayOfTheWeek.value,
                hours: this.filterForm.controls.hours.value,
                notes: this.filterForm.controls.notes.value,
                isSortAscending: this.sortAscending,
                sortProperty: this.sortProperty,
            },
        });
    }
}
